import { useEffect, useRef, useState } from "react";
import type { NavigationConfig } from "@site-factory/config";
import { cn } from "../utils";
import { Button } from "../components/Button";
import { MainContainer } from "./MainContainer";

export interface SiteHeaderProps {
  /** Site name/logo text */
  siteName: string;
  /** Navigation configuration */
  navigation: NavigationConfig;
  /** Optional logo URL */
  logoUrl?: string;
  /** Whether header should be sticky */
  sticky?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Current path for active link styling (pass from router) */
  currentPath?: string;
  /** Optional link component (e.g., react-router Link adapter) */
  linkComponent?: (props: {
    href: string;
    className?: string;
    onClick?: () => void;
    children: React.ReactNode;
  }) => JSX.Element;
}

export function SiteHeader({
  siteName,
  navigation,
  logoUrl,
  sticky = true,
  className,
  currentPath,
  linkComponent,
}: SiteHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const LinkComponent =
    linkComponent ??
    ((props: { href: string; className?: string; children: React.ReactNode }) => (
      <a {...props} />
    ));

  const normalizePath = (path?: string) => {
    if (!path) return "/";
    // ignore hashes for active state
    if (path.startsWith("#")) return path;
    return path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;
  };

  const activePath = normalizePath(currentPath);

  const linkClassName = (href: string) =>
    cn(
      "text-sm font-semibold text-foreground/80 hover:text-primary transition-colors border-b-2 border-transparent pb-1",
      normalizePath(href) === activePath && !href.startsWith("#") && "text-primary border-primary"
    );

  const closeMobile = () => setMobileOpen(false);

  // Close on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e: MouseEvent) => {
      if (!headerRef.current) return;
      if (!headerRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  return (
    <header
      className={cn(
        "relative w-full border-b border-neutral-200/80",
        "backdrop-blur supports-[backdrop-filter]:bg-background/80 bg-gradient-to-r from-primary/5 via-background/90 to-secondary/5",
        sticky && "sticky top-0 left-0 right-0 z-50 shadow-[0_10px_35px_rgba(0,0,0,0.06)]",
        className
      )}
      ref={headerRef}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_12%_10%,rgba(255,255,255,0.45),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.25),transparent_30%)]"
        aria-hidden="true"
      />

      <MainContainer className="relative">
        <div className="flex h-16 items-center justify-between">
          {/* Logo / Site Name */}
          <LinkComponent href="/" className="flex items-center gap-2">
            {logoUrl ? <img src={logoUrl} alt={siteName} className="h-8 w-auto" /> : <span className="text-xl font-bold text-primary">{siteName}</span>}
          </LinkComponent>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navigation.links.map((link) => (
              <LinkComponent key={link.href} href={link.href} className={linkClassName(link.href)}>
                {link.label}
              </LinkComponent>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {navigation.primaryCta && (
              <Button asChild variant="primary" size="sm">
                <LinkComponent href={navigation.primaryCta.href}>
                  {navigation.primaryCta.label}
                </LinkComponent>
              </Button>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-neutral-100"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
          >
            <span className="sr-only">Toggle menu</span>
            <div className="space-y-1">
              <span className={cn("block h-0.5 w-5 bg-current transition", mobileOpen && "translate-y-1.5 rotate-45")}></span>
              <span className={cn("block h-0.5 w-5 bg-current transition", mobileOpen && "opacity-0")}></span>
              <span className={cn("block h-0.5 w-5 bg-current transition", mobileOpen && "-translate-y-1.5 -rotate-45")}></span>
            </div>
          </button>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div className="md:hidden border-t border-neutral-200 bg-background px-4 pb-4 pt-2 space-y-3">
            <div className="flex flex-col gap-2">
              {navigation.links.map((link) => (
                <LinkComponent
                  key={link.href}
                  href={link.href}
                  className={cn(linkClassName(link.href), "py-2")}
                  onClick={closeMobile}
                >
                  {link.label}
                </LinkComponent>
              ))}
            </div>
            {navigation.primaryCta && (
              <Button asChild variant="primary" className="w-full" onClick={closeMobile}>
                <LinkComponent href={navigation.primaryCta.href}>
                  {navigation.primaryCta.label}
                </LinkComponent>
              </Button>
            )}
          </div>
        )}
      </MainContainer>
    </header>
  );
}
